import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';

const GoalBasedInvestment = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const slides = [
    {
      title: "Set financial goals that truly matter",
      description: "Prioritize investments based on your life goals like buying a house, child education, or retirement.",
      stats: {
        portfolio: "+18.45%",
        benchmark: "+15.20%"
      }
    },
    {
      title: "Get personalized investment strategy",
      description: "Our algorithms craft a goal-based strategy tailored to your timeline and risk tolerance.",
      stats: {
        portfolio: "+20.12%",
        benchmark: "+16.50%"
      }
    },
    {
      title: "Track progress & stay on course",
      description: "Easily monitor your journey and make timely adjustments with expert assistance.",
      stats: {
        portfolio: "+17.78%",
        benchmark: "+16.10%"
      }
    }
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="w-full bg-white">
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-[#113262] text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight text-center mb-8 lg:mb-12">
          Start a Goal Based Investment
        </h2>


        {isMobile ? (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 transition-all duration-300">
              <h3 className="text-lg font-semibold text-[#113262]">
                {slides[activeSlide].title}
              </h3>
              <p className="mt-2 text-sm text-[#545454]">
                {slides[activeSlide].description}
              </p>

              <div className="mt-6 bg-gray-50 rounded-xl p-4 sm:p-6 shadow-inner w-full">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="h-8 w-8 sm:h-10 sm:w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </div>
                      <span className="font-medium text-sm sm:text-base">Your Portfolio</span>
                    </div>
                    <span className={`font-semibold text-lg sm:text-xl ${parseFloat(slides[activeSlide].stats.portfolio) > parseFloat(slides[activeSlide].stats.benchmark)
                        ? 'text-green-500'
                        : 'text-red-500'
                      }`}>
                      {slides[activeSlide].stats.portfolio}
                    </span>
                  </div>

                  <div className="flex items-center justify-center">
                    <span className="px-3 sm:px-4 py-1 sm:py-2 bg-blue-600 text-white rounded-full text-xs sm:text-sm">
                      vs
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="h-8 w-8 sm:h-10 sm:w-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <span className="font-medium text-sm sm:text-base">Goal Benchmark</span>
                    </div>
                    <span className="text-gray-600 font-semibold text-lg sm:text-xl">
                      {slides[activeSlide].stats.benchmark}
                    </span>
                  </div>
                </div>

                <div className="mt-6 text-center text-xs sm:text-sm text-gray-500">
                  For illustrative purposes only. Past performance does not guarantee future returns.
                </div>
              </div>
            </div>

            <div className="flex justify-center gap-2">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveSlide(idx)}
                  className={`h-2.5 w-2.5 rounded-full transition-all duration-300 ${activeSlide === idx ? 'bg-[#1C52A0]' : 'bg-gray-300'
                    }`}
                />
              ))}
            </div>

            <div className="text-center">
              <a
                href="https://club.maxiomwealth.com/goals/finplan/create"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full px-6 py-3 bg-gradient-to-r from-[#1C52A0] to-[#0A1E3A] text-white rounded-lg hover:scale-105 hover:shadow-xl shadow-lg transition-all duration-300 group"
              >
                <span className="font-semibold">Plan my investments</span>
                <ArrowRight className="ml-3 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-200" />
              </a>
            </div>
          </div>
        ) : (
          // Desktop View (leave your original code here)






          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Info Card - Hidden on mobile, visible on larger screens */}
            <div className="hidden lg:block">
              <div className="bg-[#F5F5F5] rounded-xl p-6 shadow-lg space-y-6 w-full">
                <div className="flex items-center space-x-4 border-b border-gray-100 pb-4">
                  <div className="h-12 w-12 bg-[#E8EEF6] rounded-full flex items-center justify-center">
                    <svg className="h-6 w-6 text-[#1C52A0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-[#113262]">OmniMax Benefits</h4>
                    <p className="text-sm text-[#436FB0]">Smart Goal Planning Tool</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg">
                    <div className="text-sm text-[#436FB0]">Investment Period</div>
                    <div className="text-lg font-medium text-[#113262] mt-1">1-30 Years</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <div className="text-sm text-[#436FB0]">Min. Investment</div>
                    <div className="text-lg font-medium text-[#113262] mt-1">₹5,000</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <svg className="h-5 w-5 text-[#1C52A0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-[#262626]">Smart Goal Tracking</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <svg className="h-5 w-5 text-[#1C52A0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-[#262626]">Auto Portfolio Rebalancing</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <svg className="h-5 w-5 text-[#1C52A0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-[#262626]">Regular Progress Updates</span>
                  </div>
                </div>

                <div className="text-xs text-center text-gray-500 mt-4">
                  AI-powered goal planning for optimal returns
                </div>
              </div>
            </div>

            {/* Mobile version of the info card - Only visible on mobile devices */}
            <div className="lg:hidden w-full">
              <div className="bg-[#F5F5F5] rounded-xl p-4 shadow-lg space-y-4">
                <div className="flex items-center space-x-3 border-b border-gray-100 pb-3">
                  <div className="h-10 w-10 bg-[#E8EEF6] rounded-full flex items-center justify-center">
                    <svg className="h-5 w-5 text-[#1C52A0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-base font-medium text-[#113262]">OmniMax Benefits</h4>
                    <p className="text-xs text-[#436FB0]">Smart Goal Planning Tool</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white p-3 rounded-lg">
                    <div className="text-xs text-[#436FB0]">Investment Period</div>
                    <div className="text-sm font-medium text-[#113262] mt-1">1-30 Years</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <div className="text-xs text-[#436FB0]">Min. Investment</div>
                    <div className="text-sm font-medium text-[#113262] mt-1">₹5,000</div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <svg className="h-4 w-4 text-[#1C52A0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-xs text-[#262626]">Smart Goal Tracking</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg className="h-4 w-4 text-[#1C52A0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-xs text-[#262626]">Auto Portfolio Rebalancing</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg className="h-4 w-4 text-[#1C52A0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-xs text-[#262626]">Regular Progress Updates</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content - Slides */}
            <div className="space-y-6 lg:space-y-8 w-full">
              <div className="space-y-6 relative">
                {/* Progress Indicators Container */}
                <div className="absolute right-0 h-full flex flex-col">
                  {[0, 1, 2].map((index) => (
                    <div
                      key={index}
                      className={`w-1 flex-1 transition-all duration-300 rounded-none ${index === activeSlide ? 'bg-[#1C52A0]' : 'bg-[#E8EEF6]'
                        }`}
                    />
                  ))}
                </div>

                {slides.map((slide, index) => (
                  <div
                    key={index}
                    onClick={() => setActiveSlide(index)}
                    className={`pr-6 transition-all duration-500 cursor-pointer hover:opacity-90 text-left ${index === activeSlide
                        ? 'opacity-100 transform translate-y-0 scale-[1.02]'
                        : 'opacity-60 transform translate-y-2'
                      }`}
                  >
                    <h3 className={`text-base sm:text-lg font-medium ${index === activeSlide ? 'text-[#113262]' : 'text-[#436FB0]'
                      }`}>
                      {slide.title}
                    </h3>
                    <p className={`mt-2 text-sm sm:text-base ${index === activeSlide ? 'text-[#262626]' : 'text-[#545454]'
                      }`}>
                      {slide.description}
                    </p>
                  </div>
                ))}
              </div>

              <div className="text-center">
                <a href='https://club.maxiomwealth.com/goals/finplan/create' className="inline-flex items-center justify-center w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#1C52A0] to-[#0A1E3A] text-white rounded-lg hover:scale-105 hover:shadow-xl shadow-lg transition-all duration-300 group">
                  <span>Start Goal Based Investment</span>
                  <ArrowRight className="ml-2 sm:ml-3 h-4 sm:h-5 w-4 sm:w-5 transform group-hover:translate-x-1 transition-transform duration-200" />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoalBasedInvestment;